import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX } from 'react-icons/fi';

interface EditBudgetCategoryModalProps {
  isOpen: boolean;
  editingCategory: any;
  onClose: () => void;
  onSubmit: (e: React.FormEvent) => void;
  catEditName: string;
  setCatEditName: (val: string) => void;
  catEditAllocated: string;
  setCatEditAllocated: (val: string) => void;
  catEditNotes: string;
  setCatEditNotes: (val: string) => void;
}

export const EditBudgetCategoryModal: React.FC<EditBudgetCategoryModalProps> = ({
  isOpen,
  editingCategory,
  onClose,
  onSubmit,
  catEditName,
  setCatEditName,
  catEditAllocated,
  setCatEditAllocated,
  catEditNotes,
  setCatEditNotes,
}) => {
  return (
    <AnimatePresence>
      {isOpen && editingCategory && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/80 backdrop-blur-md"
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="relative w-full max-w-lg bg-[#0B0B0B] border border-white/10 rounded-[28px] p-6 text-left flex flex-col max-h-[85vh] z-10 shadow-[0_20px_50px_rgba(0,0,0,0.8)]"
          >
            <div className="flex items-start justify-between mb-5">
              <div>
                <h2 className="text-xl font-bold text-white">Edit Budget</h2>
                <p className="text-zinc-550 text-xs mt-1">
                  Update the allocated budget for this category.
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="p-1.5 rounded-full border border-white/10 hover:border-white/20 bg-zinc-900/60 hover:bg-zinc-800 text-zinc-400 hover:text-white transition-all cursor-pointer"
              >
                <FiX className="w-4 h-4" />
              </button>
            </div>

            {/* Stats Preview Card */}
            <div className="bg-[#0F0F0F]/65 border border-white/5 rounded-2xl p-4 grid grid-cols-3 text-center mb-5">
              <div>
                <p className="text-[10px] text-zinc-550 font-bold uppercase tracking-wider">
                  Spent
                </p>
                <p className="text-white text-base font-bold mt-1.5">
                  ${editingCategory.spent.toLocaleString()}
                </p>
              </div>
              <div>
                <p className="text-[10px] text-zinc-550 font-bold uppercase tracking-wider">
                  Utilization
                </p>
                <p className="text-emerald-450 text-base font-bold mt-1.5">
                  {catEditAllocated && parseFloat(catEditAllocated) > 0
                    ? Math.round((editingCategory.spent / parseFloat(catEditAllocated)) * 100)
                    : 0}
                  %
                </p>
              </div>
              <div>
                <p className="text-[10px] text-zinc-550 font-bold uppercase tracking-wider">
                  Remaining
                </p>
                <p className="text-emerald-450 text-base font-bold mt-1.5">
                  $
                  {Math.max(
                    0,
                    (parseFloat(catEditAllocated) || 0) - editingCategory.spent,
                  ).toLocaleString()}
                </p>
              </div>
            </div>

            <form onSubmit={onSubmit} className="space-y-4">
              <div>
                <label className="block text-zinc-400 text-xs font-semibold mb-2">
                  Category Name
                </label>
                <input
                  type="text"
                  value={catEditName}
                  onChange={(e) => setCatEditName(e.target.value)}
                  required
                  className="w-full bg-[#0F0F0F] border border-white/5 hover:border-white/10 focus:border-blue-500 outline-none text-white text-xs rounded-xl px-4 py-3.5 transition-all"
                />
              </div>

              <div>
                <label className="block text-zinc-400 text-xs font-semibold mb-2">
                  Allocated Amount
                </label>
                <input
                  type="number"
                  value={catEditAllocated}
                  onChange={(e) => setCatEditAllocated(e.target.value)}
                  required
                  className="w-full bg-[#0F0F0F] border border-white/5 hover:border-white/10 focus:border-blue-500 outline-none text-white text-xs rounded-xl px-4 py-3.5 transition-all"
                />
              </div>

              <div>
                <label className="block text-zinc-400 text-xs font-semibold mb-2">
                  Notes <span className="text-zinc-550">(Optional)</span>
                </label>
                <textarea
                  placeholder="Add notes or breakdown..."
                  value={catEditNotes}
                  onChange={(e) => setCatEditNotes(e.target.value)}
                  rows={4}
                  className="w-full bg-[#0F0F0F] border border-white/5 hover:border-white/10 focus:border-blue-500 outline-none text-white text-xs rounded-xl px-4 py-3.5 resize-none transition-all"
                />
              </div>

              <div className="flex items-center justify-end pt-4 border-t border-white/5">
                <button
                  type="submit"
                  className="px-6 py-2.5 bg-white text-black hover:bg-zinc-100 rounded-full text-xs font-bold cursor-pointer transition-all active:scale-95 shadow-lg shadow-white/5"
                >
                  Save Changes
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};
